import { AxiosError } from 'axios';
import { AppError } from '@/shared/helpers/AppError';

import { useAlertContext } from '@/contexts/alert.context.tsx';

interface ApiErrorResponse {
  success?: boolean;
  errorMessage?: string;
  message?: string;
  errors?: string[];
  statusCode?: number;
}

const HTTP_ERROR_MESSAGES: Record<number, string> = {
  400: 'Dados inválidos. Verifique as informações e tente novamente.',
  401: 'Sessão expirada. Faça login novamente.',
  403: 'Você não tem permissão para realizar esta ação.',
  404: 'Recurso não encontrado.',
  409: 'Conflito de dados. O registro pode já existir.',
  422: 'Dados inválidos. Verifique os campos obrigatórios.',
  429: 'Muitas tentativas. Aguarde um momento e tente novamente.',
  500: 'Erro interno do servidor. Tente novamente mais tarde.',
  502: 'Servidor temporariamente indisponível. Tente novamente.',
  503: 'Serviço em manutenção. Tente novamente em alguns minutos.',
  504: 'Tempo limite excedido. Verifique sua conexão e tente novamente.',
};

function isNetworkError(error: AxiosError): boolean {
  return !error.response && (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED');
}

export function useErrorHandler() {
  const { notify } = useAlertContext();

  const handleError = (error: unknown, defaultMessage?: string) => {
    const isAppError = error instanceof AppError;
    const isAxiosError = error instanceof AxiosError;
    const isError = error instanceof Error;

    let message: string;
    let title: string | undefined;

    if (isAxiosError) {
      if (isNetworkError(error)) {
        title = 'Erro de conexão';
        message = 'Verifique sua conexão com a internet e tente novamente.';
      }
      else if (error.code === 'ECONNABORTED') {
        title = 'Tempo esgotado';
        message = 'A requisição demorou muito. Tente novamente.';
      }
      else if (error.response?.data) {
        const apiError = error.response.data as ApiErrorResponse;
        const statusCode = error.response.status;

        if (apiError.errors && apiError.errors.length > 0) {
          message = apiError.errors.join('\n');
        } else if (apiError.errorMessage) {
          message = apiError.errorMessage;
        } else if (apiError.message) {
          message = apiError.message;
        } else {
          message = HTTP_ERROR_MESSAGES[statusCode] || defaultMessage || 'Falha na requisição.';
        }

        if (statusCode === 403) {
          title = 'Acesso negado';
        } else if (statusCode >= 500) {
          title = 'Erro do servidor';
        }
      } else {
        message = defaultMessage ?? 'Falha na requisição.';
      }
    } else if (isAppError) {
      message = error.message;
    } else if (isError) {
      message = error.message;
    } else {
      message = defaultMessage ?? 'Falha na requisição.';
    }

    notify({
      message,
      type: 'error',
      title,
    });
  };

  return {
    handleError,
  };
}
